import {Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {UrlService} from './url.service';
import {EmptyRowService} from './empty-row.service';
import {Company, Office, Theme} from '../types/admin-data.types';
import {ConstantsService} from './constants.service';
import {CustomStyleService} from './custom-style.service';
import {Observable} from 'rxjs/Observable';
import {flatMap, tap} from 'rxjs/operators';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';

declare const toastr: any;
declare const swal: any;
declare const $: any;

@Injectable()
export class AdminService {
    spinnerShow = false;

    themes: Theme[] = [];
    themesTotal: number | null = null;
    themeInModal: Theme;
    isShowThemeModal = false;

    companies: Company[] = [];
    companiesTotal: number | null = null;
    isShowCompanyModal = false;
    isShowCompanyDetailedView = false;

    isShowOfficeModal = false;
    officeInOfficeModal: Office;
    allServices = {
        'bldn': 'Building Directory',
        'email_merge': 'Email Merge',
        'service_request': 'Service Request',
        'documents': 'Documents',
        'parcels': 'Parcels',
        'pass_on_log': 'Pass on Log',
        'inc_report': 'Incident Report',
        'daily_report': 'Daily Report',
        'key_log': 'Key Log',
        'auth': 'Administration',
        'sms': 'SMS'
    };
    private themeNameToThemeMap: Map<string, Theme>;

    constructor(public api: ApiClientService,
                public urlService: UrlService,
                public emptyRowService: EmptyRowService,
                private customStyle: CustomStyleService) {
        this.getAllServices();
    }

    /** Themes  */
    getThemes() {
        this.getThemesByQueryParams().subscribe();
    }

    getThemesByQueryParams() {
        const queryParams = this.urlService.getQueryParamsString();
        const url = `/api/v1/theme.json?${queryParams}&expand=createdBy`;
        return this.api.getJson('oauthUrl', url)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity<any, Theme>(i => new Theme(+i.id, i.name, i.logo, i.settings, i.companies, i.createdAt, i.createdBy)),
                tap(({loading}) => this.spinnerShow = loading),
                tap(({entities}) => {
                    this.themeNameToThemeMap = new Map<string, Theme>(
                        entities.map((entity: Theme) => {
                            return [entity.name, entity] as [string, Theme];
                        })
                    );
                }),
                tap(
                    res => {
                        this.themes = res.entities;
                        this.themesTotal = res.total;
                        this.emptyRowService.fillEmptyRow();
                        if ($('.table')[0])
                            $('.table')[0].scrollIntoView();
                    },
                    error => this.api.errorHandler(error, 'Failed get Themes')
                )
            );
    }

    getAllThemes(): Observable<Array<any>> {
        this.spinnerShow = true;
        const url = `/api/v1/theme.json?per-page=1000`;
        return this.api.get('oauthUrl', url).map(
            res => {
                const body = JSON.parse(res._body);
                this.spinnerShow = false;
                return body.entities.map(i => new Theme(+i.id, i.name, i.logo, i.settings, i.companies));
            },
            error => this.api.errorHandler(error, 'Failed get Themes')
        );
    }

    getAllServices() {
        const url = `/api/v1/all_services.json`;
        return this.api.get('oauthUrl', url).subscribe(
            res => {
                const body = JSON.parse(res._body);
                this.allServices = body;
                return body;
            },
            error => this.api.errorHandler(error, 'Failed get Services')
        );
    }

    editTheme(theme: Theme) {
        this.isShowThemeModal = true;
        this.themeInModal = Object.assign({}, theme);
    }

    removeTheme(id: number): void {
        if (!id) return;
        swal(ConstantsService.getSwalConfig('Delete Theme'),
            () => {
                const url = `/api/v1/theme/${id}.json`;
                this.api.remove('oauthUrl', url)
                    .pipe(
                        tap(
                            () => toastr.success('Theme has been deleted.'),
                            (error) => this.api.errorHandler(error, 'Delete data failed')
                        ),
                        tap(() => {
                            if (this.themes.length - 1 == 0 && this.urlService.queryParams.page != '1') {
                                this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                            }
                        }),
                        flatMap(
                            () => this.getThemesByQueryParams()
                        ),
                        tap(
                            () => {
                                if (this.customStyle.getCurrentThemeId() === id) {
                                    const defaultTheme = this.themeNameToThemeMap.get('Default');
                                    this.customStyle.enableTheme(defaultTheme);
                                }
                            }
                        ),
                    )
                    .subscribe();
            }
        );
    }

    /** Companies  */
    getCompanies(checkedId?: number) {
        this.spinnerShow = true;
        if (!checkedId) this.isShowCompanyDetailedView = false;
        const queryParams = this.urlService.getQueryParamsString();
        const url = `/api/v1/company.json?expand=offices.members${queryParams}`;
        this.api.get('oauthUrl', url).subscribe(
            res => {
                this.companies = [];
                const body = JSON.parse(res._body);
                body.entities.forEach(i => this.companies.push(new Company(i, checkedId === +i.id)));
                this.companiesTotal = body.total;
                this.emptyRowService.fillEmptyRow();
                this.spinnerShow = false;
            },
            error => this.api.errorHandler(error, 'Failed get Themes')
        );
    }

    removeCompany(id: number): void {
        if (!id) return;
        swal(ConstantsService.getSwalConfig('Delete Company'),
            () => {
                const url = `/api/v1/company/${id}`;
                this.api.remove('oauthUrl', url).subscribe(
                    () => {
                        toastr.success('Company has been deleted.');
                        this.isShowCompanyDetailedView = false;
                        if (this.companies.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.getCompanies();
                    },
                    error => this.api.errorHandler(error, 'Delete Company failed')
                );
            }
        );
    }

    toggleCheckCompany(item: Company) {
        const checked = item.checked;
        this.companies.forEach(i => i.checked = false);
        if (checked) {
            this.isShowCompanyDetailedView = false;
        } else {
            item.checked = true;
            this.isShowCompanyDetailedView = true;
        }
    }

    /** Offices  */
    addOffice(id: number) {
        this.officeInOfficeModal = new Office();
        this.officeInOfficeModal.company = id;
        this.isShowOfficeModal = true;
    }

    editOffice(office: Office) {
        this.officeInOfficeModal = Object.assign({}, office);
        this.officeInOfficeModal.members = office.members.slice();
        this.isShowOfficeModal = true;
    }

    deleteOffice(office: Office) {
        swal(ConstantsService.getSwalConfig('Delete Office'),
            () => {
                const url = `/api/v1/office/${office.id}`;
                this.api.remove('oauthUrl', url).subscribe(
                    () => {
                        toastr.success('Office has been deleted.');
                        this.getCompanies(office.company);
                    },
                    error => this.api.errorHandler(error, 'Delete Office failed')
                );
            }
        );
    }

}



// WEBPACK FOOTER //
// /app/src/app/services/admin.service.ts